import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SWPCalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState(5000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(30000);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [withdrawalPeriod, setWithdrawalPeriod] = useState(20);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const inv = Number(initialInvestment) || 0;
    const withdraw = Number(monthlyWithdrawal) || 0;
    const ret = Number(expectedReturn) || 0;
    const period = Number(withdrawalPeriod) || 0;
    
    const monthlyReturn = ret / 12 / 100;
    const months = period * 12;
    
    let balance = inv;
    let totalWithdrawn = 0;
    let yearlyData = [];
    
    for (let month = 1; month <= months; month++) {
      // Add returns
      const returns = balance * monthlyReturn;
      balance += returns;
      
      // Withdraw
      if (balance >= withdraw) {
        balance -= withdraw;
        totalWithdrawn += withdraw;
      } else if (balance > 0) {
        totalWithdrawn += balance;
        balance = 0;
      }
      
      // Record yearly data
      if (month % 12 === 0) {
        yearlyData.push({
          year: month / 12,
          balance: Math.max(0, balance),
          totalWithdrawn,
        });
      }
    }

    // Calculate max sustainable withdrawal
    // Using formula: PMT = PV * r / (1 - (1 + r)^-n)
    const maxWithdrawal = inv * monthlyReturn / (1 - Math.pow(1 + monthlyReturn, -months));
    
    // Calculate how long corpus will last
    let corpusLastsMonths = 0;
    let tempBalance = inv;
    while (tempBalance > 0 && corpusLastsMonths < 1200) { // Max 100 years
      tempBalance = tempBalance * (1 + monthlyReturn) - withdraw;
      if (tempBalance > 0) corpusLastsMonths++;
      else break;
    }

    const corpusLastsYears = corpusLastsMonths / 12;
    const willCorpusLast = corpusLastsMonths >= months;

    setResult({
      initialInvestment,
      monthlyWithdrawal,
      totalWithdrawn,
      finalBalance: Math.max(0, balance),
      yearlyData,
      maxWithdrawal,
      corpusLastsYears: corpusLastsYears.toFixed(1),
      corpusLastsMonths,
      willCorpusLast,
      totalReturns: totalWithdrawn + Math.max(0, balance) - initialInvestment,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('SWP Calculator Report', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Parameter', 'Value']],
      body: [
        ['Initial Investment', formatCurrencyForPDF(result.initialInvestment)],
        ['Monthly Withdrawal', formatCurrencyForPDF(result.monthlyWithdrawal)],
        ['Expected Return', `${expectedReturn}% p.a.`],
        ['Withdrawal Period', `${withdrawalPeriod} years`],
        ['Total Withdrawn', formatCurrencyForPDF(result.totalWithdrawn)],
        ['Final Balance', formatCurrencyForPDF(result.finalBalance)],
        ['Corpus Lasts For', `${result.corpusLastsYears} years`],
        ['Maximum Sustainable Withdrawal', formatCurrencyForPDF(result.maxWithdrawal)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    doc.text('Year-wise Balance', 14, finalY);

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Year', 'Balance (Rs.)', 'Total Withdrawn (Rs.)']],
      body: result.yearlyData.map(item => [
        `Year ${item.year}`,
        formatCurrencyForPDF(item.balance),
        formatCurrencyForPDF(item.totalWithdrawn),
      ]),
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
    });

    doc.save('SWP_Report.pdf');
  };

  const chartData = result ? {
    labels: result.yearlyData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: 'Remaining Balance',
        data: result.yearlyData.map(d => d.balance),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="SWP Calculator"
        description="Free SWP Calculator - Calculate Systematic Withdrawal Plan. Plan monthly income from investments."
        keywords="SWP calculator, systematic withdrawal plan calculator, SWP mutual fund calculator, monthly income from mutual fund"
        canonicalPath="/swp"
      />
      <div className="calculator-header">
        <h1>💸 SWP Calculator</h1>
        <p>Systematic Withdrawal Plan - Regular income from your investments</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Initial Investment (₹)</label>
            <input
              type="number"
              value={initialInvestment === '' ? '' : initialInvestment}
              onChange={(e) => setInitialInvestment(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Monthly Withdrawal (₹)</label>
            <input
              type="number"
              value={monthlyWithdrawal === '' ? '' : monthlyWithdrawal}
              onChange={(e) => setMonthlyWithdrawal(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Expected Return (% p.a.)</label>
              <input
                type="number"
                step="0.5"
                value={expectedReturn === '' ? '' : expectedReturn}
                onChange={(e) => setExpectedReturn(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Withdrawal Period (Years)</label>
              <input
                type="number"
                value={withdrawalPeriod === '' ? '' : withdrawalPeriod}
                onChange={(e) => setWithdrawalPeriod(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate SWP
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ 
                background: result.willCorpusLast 
                  ? 'linear-gradient(135deg, #10b981, #059669)'
                  : 'linear-gradient(135deg, #ef4444, #dc2626)'
              }}>
                <h3>Corpus Will Last</h3>
                <div className="amount">{result.corpusLastsYears} Years</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  {result.willCorpusLast 
                    ? '✅ Your corpus will sustain the planned withdrawals'
                    : '⚠️ Corpus will deplete before planned period'}
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Initial Investment</span>
                  <span className="value">{formatCurrency(result.initialInvestment)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Monthly Withdrawal</span>
                  <span className="value">{formatCurrency(result.monthlyWithdrawal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Withdrawn</span>
                  <span className="value">{formatCurrency(result.totalWithdrawn)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Final Balance</span>
                  <span className="value">{formatCurrency(result.finalBalance)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Returns Earned</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.totalReturns)}</span>
                </div>
                <div className="result-item" style={{ background: '#dbeafe' }}>
                  <span className="label">Max Sustainable Withdrawal</span>
                  <span className="value" style={{ color: '#2563eb', fontWeight: '600' }}>{formatCurrency(result.maxWithdrawal)}/month</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Balance Over Time</h4>
                  <Line data={chartData} options={{ 
                    responsive: true, 
                    plugins: { legend: { position: 'bottom' } },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (value) => '₹' + (value / 100000).toFixed(0) + 'L'
                        }
                      }
                    }
                  }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your investment details to calculate SWP</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>💡 SWP Benefits</h4>
        <ul>
          <li>Regular monthly income from your investments</li>
          <li>Tax efficient - only gains are taxed, not principal</li>
          <li>LTCG on equity funds above ₹1.25L taxed at 12.5%</li>
          <li>Better than FD interest for retirees in higher tax brackets</li>
          <li>Rupee cost averaging on withdrawal</li>
        </ul>
      </div>
    </div>
  );
};

export default SWPCalculator;
